import axios from 'axios'
import { authInstance } from '@/services/AuthService.js'
import Config from '@/config.json'
import store from '@/store/index'
import jwt_decode from 'jwt-decode'
import * as Sentry from '@sentry/vue'

let instance = null
let unloggedInstance = null

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

export default class Api {
  /**
   * 
   * @param {Boolean} unloggedInstance - true if instance used for not protected api
   */
  constructor(unloggedInstance) {
    const auth = authInstance()
    const authorizationHeader = 'Authorization'

    const sentryApiCatch = error => {
      if (unloggedInstance) {
        Sentry.configureScope(scope =>
          scope
            .setLevel('error')
            .setExtra('config', error.config))
      } else {
        auth.getUser().then(user => {
          if (user) {
            Sentry.configureScope(scope =>
              scope
                .setLevel('error')
                .setUser({
                  uuid: user.profile.attributes.uuid,
                  username: `${user.profile.given_name} ${user.profile.family_name}`,
                  email: user.profile.email,
                })
                .setExtra('config', error.config),
            )
          }
        })
      }

      return Sentry.captureException(error)
    }

    const sessionExpired = () => {
      console.log('You are not authorized')
      store.commit('userStates/updateToken', null)
      store.commit('userStates/updateRefreshToken', null)
      store.commit('userStates/updateProfileId', null)
      store.commit('userStates/updateUserTenant', null)
      store.commit('userStates/updateUserFeatureFlags', {})
      store.commit('userStates/updateLoggedIn', false)
      store.commit('userStates/updateUser', {})
      auth.logout()
    }

    // create an axios instance with default options
    this.axiosInstance = axios.create({
      baseURL: Config.VUE_APP_BACKEND_URL,
    })

    if (!unloggedInstance) {
      // before each api call, check if user has valid access token
      this.axiosInstance.interceptors.request.use(async config => {

        // Get access token
        const data = await auth.getAccessToken()

        if (!data?.access_token && !data?.refresh_token) {
          return Promise.reject(config)
        }

        // If request for get new token then do not check if is refreshing access token or if token is expired
        const url = config.url.toLowerCase()
        if (url.indexOf('auth/oidc/accesstoken') > -1 && data?.access_token) {
          config.headers.common[
            authorizationHeader
          ] = `Bearer ${data.access_token}`
          return Promise.resolve(config)
        }

        // before send request check if access token is expired
        const decodedToken = jwt_decode(data.access_token) || {}
        const timeNow = parseInt((new Date().getTime()) / 1000, 10)
        const expTime = decodedToken?.exp || 0

        // if is refreshing token (because token is experied OR there is an error in response)
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject })
          })
            .then(token => {
              config.headers['Authorization'] = `Bearer ${token}`
              return Promise.resolve(config)
            })
            .catch(err => {
              return Promise.reject(err)
            })
        }

        // Check if token expired
        if (expTime < timeNow) {
          // set refreshing to true
          isRefreshing = true

          // get new access token and refresh token
          const newTokens = await auth.renewToken()

          return new Promise(function (resolve, reject) {
            try {
              // Process all queued requests with new access token
              if (!newTokens?.access_token) throw new Error('Unauthorized')
              processQueue(null, newTokens.access_token)
              // Process this response with new access token
              config.headers['Authorization'] = `Bearer ${newTokens.access_token}`
              resolve(Promise.resolve(config))
            } catch (err) {
              processQueue(err, null)
              sessionExpired()
              reject(Promise.reject(err))
            } finally {
              isRefreshing = false
            }
          })
        } else {
          if (data.access_token) {
            config.headers.common[
              authorizationHeader
            ] = `Bearer ${data.access_token}`
          }
          return Promise.resolve(config)
        }
      }, function (error) {
        console.log({ error })
        sentryApiCatch(error)
        return Promise.reject(error)
      })
      // both response and error are being caught and checked for 401 unauthorized
      this.axiosInstance.interceptors.response.use(
        response => {
          if (response.status === 401) {
            sessionExpired()
          }
          return response
        },
        async error => {
          if (process.env.NODE_ENV !== 'development') {
            console.log({ error })
            sentryApiCatch(error)
          }

          const originalRequest = error.config
          const errorStatus = error?.response?.status
          if (errorStatus === 401 && !originalRequest._retry && !unloggedInstance) {
            if (isRefreshing) {
              return new Promise(function (resolve, reject) {
                failedQueue.push({ resolve, reject })
              })
                .then(token => {
                  if (token) originalRequest.headers['Authorization'] = `Bearer ${token}`
                  return instance(originalRequest)
                })
                .catch(err => {
                  return Promise.reject(err)
                })
            }
      
            originalRequest._retry = true
            isRefreshing = true

            const newTokens = await auth.renewToken()

            return new Promise(function (resolve, reject) {
              try {
                if (!newTokens?.access_token) throw new Error('Unauthorized')
                instance.defaults.headers.common['Authorization'] = `Bearer ${newTokens.access_token}`
                originalRequest.headers['Authorization'] = `Bearer ${newTokens.access_token}`

                processQueue(null, newTokens.access_token)
                resolve(instance(originalRequest))
              } catch (err) {
                processQueue(err, null)
                sessionExpired()
                reject(err)
              } finally {
                isRefreshing = false
              }
            })
          } else if (errorStatus === 401 && originalRequest._retry && !unloggedInstance) {
            sessionExpired()
          }

          if (error.response) {
            return Promise.reject(error.response.data)
          }

          return Promise.reject({
            ...error,
            message:
              "Something is wrong, don't worry and try again in few minutes",
            withoutRetry: true,
          })
        },
      )
    }
  }
}

/**
 * 
 * @returns protected api instance for logged users
 */
export const apiInstance = () => {
  return instance || (instance = new Api(false).axiosInstance)
}

/**
 * 
 * @returns non-protected api instance for not logged users
 */
export const apiUnloggedInstance = () => {
  return unloggedInstance || (unloggedInstance = new Api(true).axiosInstance)
}